import React from 'react';
import { Button } from '../ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';

export interface ConfirmationStepProps {
  /** Complete form state to display as a summary */
  readonly formState: {
    type: 'oidc' | 'saml';
    metadataUrl: string;
    clientId: string;
    clientSecret: string;
    adminGroupId: string;
    attributeMappings: Array<{ sourceClaim: string; targetProperty: string }>;
  };
  /** Callback triggered when the user confirms and submits */
  readonly onSubmit: () => void;
  /** When true, disables the submit button to prevent duplicate submissions */
  readonly isSubmitting?: boolean;
}

const IDP_TYPE_LABELS: Record<'oidc' | 'saml', string> = {
  oidc: 'OpenID Connect (OIDC)',
  saml: 'SAML 2.0 (SAML)',
};

/**
 * Masks a secret string by showing only the first 4 characters followed by asterisks.
 * If the secret is 4 characters or fewer, it is fully masked.
 */
function maskSecret(secret: string): string {
  if (secret.length <= 4) {
    return '********';
  }
  return secret.slice(0, 4) + '********';
}

/**
 * Step 4 (Confirmation) of the IdP Configuration Wizard.
 * Displays a read-only summary of all configured values for review before submission.
 * The client secret is masked to avoid accidental exposure.
 */
export function ConfirmationStep({
  formState,
  onSubmit,
  isSubmitting = false,
}: ConfirmationStepProps): React.ReactElement {
  return (
    <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm" role="region" aria-label="Configuration summary">
      <h3 className="text-lg font-semibold leading-none tracking-tight mb-4">Review Configuration</h3>

      <dl className="space-y-3">
        <div>
          <dt className="text-sm font-semibold">Identity Provider Type</dt>
          <dd className="text-sm text-muted-foreground">{IDP_TYPE_LABELS[formState.type]}</dd>
        </div>

        <div>
          <dt className="text-sm font-semibold">Metadata URL</dt>
          <dd className="text-sm text-muted-foreground">{formState.metadataUrl}</dd>
        </div>

        <div>
          <dt className="text-sm font-semibold">Client ID</dt>
          <dd className="text-sm text-muted-foreground">{formState.clientId}</dd>
        </div>

        <div>
          <dt className="text-sm font-semibold">Client Secret</dt>
          <dd className="text-sm text-muted-foreground">{maskSecret(formState.clientSecret)}</dd>
        </div>

        {formState.adminGroupId && (
          <div>
            <dt className="text-sm font-semibold">Admin Group</dt>
            <dd className="text-sm text-muted-foreground">{formState.adminGroupId}</dd>
          </div>
        )}

        {formState.attributeMappings.length > 0 && (
          <div>
            <dt className="text-sm font-semibold mb-2">Attribute Mappings</dt>
            <dd>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Source Claim</TableHead>
                    <TableHead scope="col">Target Property</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formState.attributeMappings.map((mapping, index) => (
                    <TableRow key={index}>
                      <TableCell>{mapping.sourceClaim}</TableCell>
                      <TableCell>{mapping.targetProperty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </dd>
          </div>
        )}
      </dl>

      <Button
        type="button"
        className="mt-6 w-full"
        onClick={onSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Configuring...' : 'Confirm & Configure'}
      </Button>
    </div>
  );
}
